import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormControlLabel,
  Radio,
  FormGroup,
  Divider,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Save, Assignment, AttachFile, Delete, Download, Email } from '@mui/icons-material';
import { useAuth } from 'react-oidc-context';
import appConfig from '../config/app';

// API service for protocol-related operations
const createProtocolApiService = (auth) => ({
  async saveProtocol(rmaId, protocolType, protocolData, attachments) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    if (!protocolData || typeof protocolData !== 'object') {
      console.error('Invalid protocolData:', protocolData);
      throw new Error('protocol_data must be a valid JSON object');
    }

    const formData = new FormData();
    formData.append('protocol_type', protocolType);
    try {
      formData.append('protocol_data', JSON.stringify(protocolData));
    } catch (error) {
      console.error('Error stringifying protocolData:', error, protocolData);
      throw new Error('Failed to serialize protocol_data');
    }
    formData.append('cognito_user_id', cognito_user_id);

    attachments.forEach((file, index) => {
      formData.append(`attachment_${index}`, file);
    });

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/protocol/update/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error Response:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    return response.json();
  },

  async getRMADetails(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/details/?detailed=true`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async getProtocol(rmaId, protocolType = null) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const url = protocolType
      ? `${appConfig.api.baseUrl}/rma/${rmaId}/protocol/?protocol_type=${protocolType}`
      : `${appConfig.api.baseUrl}/rma/${rmaId}/protocol/`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async downloadProtocol(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/protocol/export`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="(.+?)"/);
    const filename = filenameMatch ? filenameMatch[1] : `rma-${rmaId}-export.pdf`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  async sendProtocolToCustomer(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;

    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/send-protocol/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    return response.json();
  },
});

const MeasurementProtocolForm = ({ rmaId, onNotification }) => {
  const auth = useAuth();
  const fileInputRef = useRef(null);
  const [protocolType, setProtocolType] = useState('loadcell');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'info' });
  const [rmaData, setRmaData] = useState(null);
  const [localAttachments, setLocalAttachments] = useState([]);
  const [savedAttachments, setSavedAttachments] = useState([]);
  const [isProtocolSaved, setIsProtocolSaved] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null); // Store last save timestamp
  const [lastSentAt, setLastSentAt] = useState(null);   // Store last send timestamp
  const [protocolData, setProtocolData] = useState({
    date: new Date().toISOString().split('T')[0],
    model: '',
    nominalCapacity: '',
    nominalSensitivity: '',
    serialNumber: '',
    installationDescription: '',
    defectDescription: '',
    visualControl: {
      label: 'ok',
      appearance: 'likeNew',
      condition: 'clean',
      mechanicalDamage: {
        dented: false,
        mechanicalHits: false,
        deformation: false,
        destroyedWeld: false,
      },
      cable: 'length',
      cableLength: '',
      notes: '',
    },
    electricalCheck: {
      supplyVoltage: '5.00',
      zeroSignal: { value: '', status: 'stable' },
      inputImpedance: { value: '', tolerance: 'inTolerance' },
      outputImpedance: { value: '', tolerance: 'inTolerance' },
      insulationImpedance: {
        bridgeBody: { value: '>5000', tolerance: 'inTolerance' },
        bridgeShield: { value: '>5000', tolerance: 'inTolerance' },
        shieldBody: { value: '>5000', tolerance: 'inTolerance' },
      },
      stabilitySmallHits: 'stable',
    },
    electronicsCheck: {
      mainTest: { value: '', tolerance: 'inTolerance' },
      digitalInputTest: { value: '', tolerance: 'inTolerance' },
      factoryReset: { value: '', tolerance: 'inTolerance' },
      fwUpdate: { value: '', tolerance: 'inTolerance' },
      notes: '',
    },
    conclusion: {
      status: 'Good',
      probableCause: {
        mechanicalOverload: false,
        electricalOverload: false,
        waterPenetration: false,
        internalComponentBroke: false,
        externalCableBad: false,
        other: { selected: false, description: '' },
      },
      notes: '',
      conclusionText: '',
    },
    serviceman: {
      name: 'Marek Němec',
      phone: '+420 546 427 053, +420 546 427 059',
      mobile: '602295711',
      mobile2: '',
      email: 'm.nemec@utilcell.com',
    },
    headOfComplaints: {
      name: 'Marek Němec',
      phone: '+420 546 427 053, +420 546 427 059',
      mobile: '602295711',
      mobile2: '',
      email: 'm.nemec@utilcell.com',
    },
  });

  const cableLengthOptions = ['0.3', '0.4', '0.5', '0.8', '4', '5', '10', '12', '15', '18', '20'];

  const apiService = createProtocolApiService(auth);

const fetchData = async () => {
  setLoading(true);
  try {
    const rmaData = await apiService.getRMADetails(rmaId);
    setRmaData(rmaData);

    const protocolResponse = await apiService.getProtocol(rmaId);

    const defaultProtocolData = {
      date: new Date().toISOString().split('T')[0],
      model: rmaData.product_name || '',
      serialNumber: rmaData.serial_number || '',
      defectDescription: rmaData.description || '',
      visualControl: {
        label: 'ok',
        appearance: 'likeNew',
        condition: 'clean',
        mechanicalDamage: {
          dented: false,
          mechanicalHits: false,
          deformation: false,
          destroyedWeld: false,
        },
        cable: 'length',
        cableLength: '',
        notes: '',
      },
      electricalCheck: {
        supplyVoltage: '5.00',
        zeroSignal: { value: '', status: 'stable' },
        inputImpedance: { value: '', tolerance: 'inTolerance' },
        outputImpedance: { value: '', tolerance: 'inTolerance' },
        insulationImpedance: {
          bridgeBody: { value: '>5000', tolerance: 'inTolerance' },
          bridgeShield: { value: '>5000', tolerance: 'inTolerance' },
          shieldBody: { value: '>5000', tolerance: 'inTolerance' },
        },
        stabilitySmallHits: 'stable',
      },
      electronicsCheck: {
        mainTest: { value: '', tolerance: 'inTolerance' },
        digitalInputTest: { value: '', tolerance: 'inTolerance' },
        factoryReset: { value: '', tolerance: 'inTolerance' },
        fwUpdate: { value: '', tolerance: 'inTolerance' },
        notes: '',
      },
      conclusion: {
        status: 'Good',
        probableCause: {
          mechanicalOverload: false,
          electricalOverload: false,
          waterPenetration: false,
          internalComponentBroke: false,
          externalCableBad: false,
          other: { selected: false, description: '' },
        },
        notes: '',
        conclusionText: '',
      },
      serviceman: {
        name: 'Marek Němec',
        phone: '+420 546 427 053, +420 546 427 059',
        mobile: '602295711',
        mobile2: '',
        email: 'm.nemec@utilcell.com',
      },
      headOfComplaints: {
        name: 'Marek Němec',
        phone: '+420 546 427 053, +420 546 427 059',
        mobile: '602295711',
        mobile2: '',
        email: 'm.nemec@utilcell.com',
      },
    };

    let savedProtocolType = protocolResponse.default_protocol_type || 'loadcell';
    let protocolDataToSet = defaultProtocolData;
    let savedAttachmentsToSet = [];
    let lastSavedTimestamp = null;
    let lastSentTimestamp = null;

    if (protocolResponse.has_saved_protocols && protocolResponse.protocols.length > 0) {
      savedProtocolType = protocolResponse.protocols[0].protocol_type;
      protocolDataToSet = {
        ...defaultProtocolData,
        ...protocolResponse.protocols[0].protocol_data,
        model: rmaData.product_name || protocolResponse.protocols[0].protocol_data.model || '',
        serialNumber: rmaData.serial_number || protocolResponse.protocols[0].protocol_data.serialNumber || '',
        defectDescription: rmaData.description || protocolResponse.protocols[0].protocol_data.defectDescription || '',
      };
      savedAttachmentsToSet = protocolResponse.protocols[0].attachments || [];
      lastSavedTimestamp = protocolResponse.protocols[0].updated_at || null;
      lastSentTimestamp = protocolResponse.protocols[0].last_sent_at || null;
      setIsProtocolSaved(true);
    }

    setProtocolType(savedProtocolType);
    setProtocolData(protocolDataToSet);
    setSavedAttachments(savedAttachmentsToSet);
    setLastSavedAt(lastSavedTimestamp);
    setLastSentAt(lastSentTimestamp);
  } catch (error) {
    showNotification(`Error fetching data: ${error.message}`, 'error');
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchData();
  }, [rmaId]);

  const setNestedValue = (obj, path, value) => {
    const keys = path.split('.');
    const lastKey = keys.pop();
    let current = obj;

    for (const key of keys) {
      current = current[key] = { ...current[key] };
    }

    current[lastKey] = value;
    return { ...obj };
  };

  const handleInputChange = (section, field, value, subfield = null) => {
    setProtocolData((prev) => {
      if (!subfield) {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value,
          },
        };
      }

      const newSectionData = setNestedValue(
        { ...prev[section] },
        `${field}.${subfield}`,
        value
      );

      return {
        ...prev,
        [section]: newSectionData,
      };
    });
  };

  const handleRadioChange = (section, field, value, subfield = null) => {
    setProtocolData((prev) => {
      if (subfield) {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: {
              ...prev[section][field],
              [subfield]: value,
            },
          },
        };
      }
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      };
    });
  };

  const handleCheckboxChange = (section, field, option, checked, subfield = null) => {
    setProtocolData((prev) => {
      if (subfield) {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: {
              ...prev[section][field],
              [option]: {
                ...prev[section][field][option],
                [subfield]: checked,
              },
            },
          },
        };
      }
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: {
            ...prev[section][field],
            [option]: checked,
          },
        },
      };
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setLocalAttachments((prev) => [...prev, ...files]);
  };

  const removeLocalAttachment = (index) => {
    setLocalAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ open: true, message, type });
    onNotification(message, type);
    setTimeout(() => setNotification({ ...notification, open: false }), 5000);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await apiService.saveProtocol(rmaId, protocolType, protocolData, localAttachments);
      showNotification('Protocol saved successfully', 'success');
      setIsProtocolSaved(true);
      await fetchData();
      setLocalAttachments([]);
    } catch (error) {
      showNotification(`Error saving protocol: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      await apiService.downloadProtocol(rmaId);
      showNotification('Protocol PDF downloaded successfully', 'success');
    } catch (error) {
      showNotification(`Error downloading protocol: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendToCustomer = async () => {
    setLoading(true);
    try {
      await apiService.sendProtocolToCustomer(rmaId);
      showNotification('Protocol sent to customer successfully', 'success');
      await fetchData(); // Refresh data to get last_sent_at
    } catch (error) {
      showNotification(`Error sending protocol to customer: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }); // e.g., "Aug 25, 2025, 12:03:00"
  };

  useEffect(() => {
    if (rmaData) {
      const fetchProtocolData = async () => {
        setLoading(true);
        try {
          const protocolResponse = await apiService.getProtocol(rmaId, protocolType);
          const defaultProtocolData = {
            date: new Date().toISOString().split('T')[0],
            model: rmaData.product_name || '',
            serialNumber: rmaData.serial_number || '',
            defectDescription: rmaData.description || '',
            visualControl: {
              label: 'ok',
              appearance: 'likeNew',
              condition: 'clean',
              mechanicalDamage: {
                dented: false,
                mechanicalHits: false,
                deformation: false,
                destroyedWeld: false,
              },
              cable: 'length',
              cableLength: '',
              notes: '',
            },
            electricalCheck: {
              supplyVoltage: '5.00',
              zeroSignal: { value: '', status: 'stable' },
              inputImpedance: { value: '', tolerance: 'inTolerance' },
              outputImpedance: { value: '', tolerance: 'inTolerance' },
              insulationImpedance: {
                bridgeBody: { value: '>5000', tolerance: 'inTolerance' },
                bridgeShield: { value: '>5000', tolerance: 'inTolerance' },
                shieldBody: { value: '>5000', tolerance: 'inTolerance' },
              },
              stabilitySmallHits: 'stable',
            },
            electronicsCheck: {
              mainTest: { value: '', tolerance: 'inTolerance' },
              digitalInputTest: { value: '', tolerance: 'inTolerance' },
              factoryReset: { value: '', tolerance: 'inTolerance' },
              fwUpdate: { value: '', tolerance: 'inTolerance' },
              notes: '',
            },
            conclusion: {
              status: 'Good',
              probableCause: {
                mechanicalOverload: false,
                electricalOverload: false,
                waterPenetration: false,
                internalComponentBroke: false,
                externalCableBad: false,
                other: { selected: false, description: '' },
              },
              notes: '',
              conclusionText: '',
            },
            serviceman: {
              name: 'Marek Němec',
              phone: '+420 546 427 053, +420 546 427 059',
              mobile: '602295711',
              mobile2: '',
              email: 'm.nemec@utilcell.com',
            },
            headOfComplaints: {
              name: 'Marek Němec',
              phone: '+420 546 427 053, +420 546 427 059',
              mobile: '602295711',
              mobile2: '',
              email: 'm.nemec@utilcell.com',
            },
          };

          if (protocolResponse.length > 0 && protocolResponse[0].protocol_data) {
            setProtocolData({
              ...defaultProtocolData,
              ...protocolResponse[0].protocol_data,
              model: rmaData.product_name || protocolResponse[0].protocol_data.model || '',
              serialNumber: rmaData.serial_number || protocolResponse[0].protocol_data.serialNumber || '',
              defectDescription: rmaData.description || protocolResponse[0].protocol_data.defectDescription || '',
            });
            setSavedAttachments(protocolResponse[0].attachments || []);
            setIsProtocolSaved(true);
          } else {
            setProtocolData(defaultProtocolData);
            setSavedAttachments([]);
            setIsProtocolSaved(false);
          }
        } catch (error) {
          showNotification(`Error fetching protocol data: ${error.message}`, 'error');
        } finally {
          setLoading(false);
        }
      };
      fetchProtocolData();
    }
  }, [protocolType, rmaData]);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {notification.open && (
        <Alert severity={notification.type} sx={{ mb: 2 }} onClose={() => setNotification({ ...notification, open: false })}>
          {notification.message}
        </Alert>
      )}

      <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, borderBottom: '2px solid #1976d2', pb: 2 }}>
            <Assignment />
            <Typography variant="h5">WARRANTY AND MEASUREMENT PROTOCOL - {rmaData?.rma_number || '_____/2021'}</Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Protocol Type</InputLabel>
              <Select
                value={protocolType}
                onChange={(e) => setProtocolType(e.target.value)}
                label="Protocol Type"
              >
                <MenuItem value="loadcell">Loadcell</MenuItem>
                <MenuItem value="electronics">Electronics</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">CUSTOMER</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company"
                  value={rmaData?.company_name || ''}
                  disabled
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Person in Charge"
                  value={rmaData?.contact_person || ''}
                  disabled
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={rmaData?.phone || ''}
                  disabled
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={rmaData?.email || ''}
                  disabled
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={protocolData.date}
                  onChange={(e) => setProtocolData((prev) => ({ ...prev, date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">MEASUREMENT PROTOCOL PERFORMED BY</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Person in Charge"
                  value={protocolData.serviceman.name}
                  disabled
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={protocolData.serviceman.phone}
                  disabled
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mobile Phone"
                  value={protocolData.serviceman.mobile}
                  disabled
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={protocolData.serviceman.email}
                  disabled
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">LOAD CELL INFORMATION</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Model"
                  value={protocolData.model || ''}
                  onChange={(e) => setProtocolData((prev) => ({ ...prev, model: e.target.value }))}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Serial Number"
                  value={protocolData.serialNumber || ''}
                  onChange={(e) => setProtocolData((prev) => ({ ...prev, serialNumber: e.target.value }))}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={protocolType === 'loadcell' ? 'Nominal Capacity' : 'Model Designation'}
                  value={protocolData.nominalCapacity || ''}
                  onChange={(e) => setProtocolData((prev) => ({ ...prev, nominalCapacity: e.target.value }))}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={protocolType === 'loadcell' ? 'Nominal Sensitivity' : 'Additional Information'}
                  value={protocolData.nominalSensitivity || ''}
                  onChange={(e) => setProtocolData((prev) => ({ ...prev, nominalSensitivity: e.target.value }))}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description of Installation, Type of Scale, etc."
                  multiline
                  rows={4}
                  value={protocolData.installationDescription || ''}
                  onChange={(e) => setProtocolData((prev) => ({ ...prev, installationDescription: e.target.value }))}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description of the Defect"
                  multiline
                  rows={4}
                  value={protocolData.defectDescription || ''}
                  onChange={(e) => setProtocolData((prev) => ({ ...prev, defectDescription: e.target.value }))}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">ATTACHMENTS (OPTIONAL)</Typography>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              startIcon={<AttachFile />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ mb: 2, textTransform: 'none' }}
            >
              Select Files
            </Button>
            {(localAttachments.length > 0 || savedAttachments.length > 0) && (
              <>
                {localAttachments.length > 0 && (
                  <>
                    <Typography variant="subtitle1" gutterBottom>Newly Selected Files (Not Yet Saved)</Typography>
                    <List dense>
                      {localAttachments.map((file, index) => (
                        <ListItem key={`local-${index}`} sx={{ px: 0 }}>
                          <ListItemText
                            primary={file.name}
                            secondary={`${(file.size / 1024).toFixed(1)} KB`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" onClick={() => removeLocalAttachment(index)} color="error" size="small">
                              <Delete />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
                {savedAttachments.length > 0 && (
                  <>
                    <Typography variant="subtitle1" gutterBottom>Saved Attachments</Typography>
                    <List dense>
                      {savedAttachments.map((attachment, index) => (
                        <ListItem key={`saved-${attachment.id}`} sx={{ px: 0 }}>
                          <ListItemText
                            primary={attachment.original_filename}
                            secondary={`${(attachment.file_size / 1024).toFixed(1)} KB • Uploaded by ${attachment.created_by} on ${new Date(attachment.created_at).toLocaleDateString()}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </>
            )}
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">VISUAL CONTROL</Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Selection</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Label</TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.visualControl.label === 'ok'}
                              onChange={() => handleRadioChange('visualControl', 'label', 'ok')}
                            />
                          }
                          label="OK"
                        />
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.visualControl.label === 'unreadable'}
                              onChange={() => handleRadioChange('visualControl', 'label', 'unreadable')}
                            />
                          }
                          label="Unreadable"
                        />
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.visualControl.label === 'missing'}
                              onChange={() => handleRadioChange('visualControl', 'label', 'missing')}
                            />
                          }
                          label="Missing"
                        />
                      </FormGroup>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Appearance of the {protocolType === 'loadcell' ? 'Sensor' : 'Device'}</TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.visualControl.appearance === 'likeNew'}
                              onChange={() => handleRadioChange('visualControl', 'appearance', 'likeNew')}
                            />
                          }
                          label="Like New"
                        />
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.visualControl.appearance === 'used'}
                              onChange={() => handleRadioChange('visualControl', 'appearance', 'used')}
                            />
                          }
                          label="Used"
                        />
                      </FormGroup>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Condition</TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.visualControl.condition === 'clean'}
                              onChange={() => handleRadioChange('visualControl', 'condition', 'clean')}
                            />
                          }
                          label="Clean"
                        />
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.visualControl.condition === 'dirt'}
                              onChange={() => handleRadioChange('visualControl', 'condition', 'dirt')}
                            />
                          }
                          label="Dirt"
                        />
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.visualControl.condition === 'rusted'}
                              onChange={() => handleRadioChange('visualControl', 'condition', 'rusted')}
                            />
                          }
                          label="Rusted"
                        />
                      </FormGroup>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Mechanical Damage</TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={protocolData.visualControl.mechanicalDamage.dented}
                              onChange={(e) => handleCheckboxChange('visualControl', 'mechanicalDamage', 'dented', e.target.checked)}
                            />
                          }
                          label="Dented"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={protocolData.visualControl.mechanicalDamage.mechanicalHits}
                              onChange={(e) => handleCheckboxChange('visualControl', 'mechanicalDamage', 'mechanicalHits', e.target.checked)}
                            />
                          }
                          label="Mechanical Hits"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={protocolData.visualControl.mechanicalDamage.deformation}
                              onChange={(e) => handleCheckboxChange('visualControl', 'mechanicalDamage', 'deformation', e.target.checked)}
                            />
                          }
                          label="Deformation"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={protocolData.visualControl.mechanicalDamage.destroyedWeld}
                              onChange={(e) => handleCheckboxChange('visualControl', 'mechanicalDamage', 'destroyedWeld', e.target.checked)}
                            />
                          }
                          label="Destroyed Weld"
                        />
                      </FormGroup>
                    </TableCell>
                  </TableRow>
                  {protocolType === 'loadcell' && (
                    <>
                      <TableRow>
                        <TableCell>Cable</TableCell>
                        <TableCell>
                          <FormGroup row>
                            <FormControlLabel
                              control={
                                <Radio
                                  checked={protocolData.visualControl.cable === 'ok'}
                                  onChange={() => handleRadioChange('visualControl', 'cable', 'ok')}
                                />
                              }
                              label="OK"
                            />
                            <FormControlLabel
                              control={
                                <Radio
                                  checked={protocolData.visualControl.cable === 'cut'}
                                  onChange={() => handleRadioChange('visualControl', 'cable', 'cut')}
                                />
                              }
                              label="Cut"
                            />
                            <FormControlLabel
                              control={
                                <Radio
                                  checked={protocolData.visualControl.cable === 'squeezed'}
                                  onChange={() => handleRadioChange('visualControl', 'cable', 'squeezed')}
                                />
                              }
                              label="Squeezed"
                            />
                          </FormGroup>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Cable Length</TableCell>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            options={cableLengthOptions}
                            value={protocolData.visualControl.cableLength || ''}
                            onChange={(event, newValue) => {
                              handleInputChange('visualControl', 'cableLength', newValue || '');
                            }}
                            onInputChange={(event, newInputValue) => {
                              handleInputChange('visualControl', 'cableLength', newInputValue);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Cable Length (m)"
                                variant="outlined"
                                size="small"
                                sx={{ minWidth: 200 }}
                              />
                            )}
                          />
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                  <TableRow>
                    <TableCell>Notes</TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={protocolData.visualControl.notes || ''}
                        onChange={(e) => handleInputChange('visualControl', 'notes', e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {protocolType === 'loadcell' ? (
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom color="primary">ELECTRICAL CHECK</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Supply Voltage (V)"
                    value={protocolData.electricalCheck.supplyVoltage || ''}
                    onChange={(e) => handleInputChange('electricalCheck', 'supplyVoltage', e.target.value)}
                  />
                </Grid>
                <Divider sx={{ my: 2, width: '100%' }} />
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Zero Voltage Signal Without Load</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Value (mV/V)"
                        value={protocolData.electricalCheck.zeroSignal.value || ''}
                        onChange={(e) => handleInputChange('electricalCheck', 'zeroSignal', e.target.value, 'value')}
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <FormGroup row>
                        <FormControlLabel
                          control={<Radio checked={protocolData.electricalCheck.zeroSignal.status === 'stable'} onChange={() => handleRadioChange('electricalCheck', 'zeroSignal', 'stable', 'status')} />}
                          label="Stable"
                        />
                        <FormControlLabel
                          control={<Radio checked={protocolData.electricalCheck.zeroSignal.status === 'unstable'} onChange={() => handleRadioChange('electricalCheck', 'zeroSignal', 'unstable', 'status')} />}
                          label="Unstable"
                        />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2, width: '100%' }} />
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Input Impedance</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Value (Ω)"
                        value={protocolData.electricalCheck.inputImpedance.value || ''}
                        onChange={(e) => handleInputChange('electricalCheck', 'inputImpedance', e.target.value, 'value')}
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <FormGroup row>
                        <FormControlLabel
                          control={<Radio checked={protocolData.electricalCheck.inputImpedance.tolerance === 'inTolerance'} onChange={() => handleRadioChange('electricalCheck', 'inputImpedance', 'inTolerance', 'tolerance')} />}
                          label="In Tolerance"
                        />
                        <FormControlLabel
                          control={<Radio checked={protocolData.electricalCheck.inputImpedance.tolerance === 'outTolerance'} onChange={() => handleRadioChange('electricalCheck', 'inputImpedance', 'outTolerance', 'tolerance')} />}
                          label="Out of Tolerance"
                        />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2, width: '100%' }} />
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Output Impedance</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Value (Ω)"
                        value={protocolData.electricalCheck.outputImpedance.value || ''}
                        onChange={(e) => handleInputChange('electricalCheck', 'outputImpedance', e.target.value, 'value')}
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <FormGroup row>
                        <FormControlLabel
                          control={<Radio checked={protocolData.electricalCheck.outputImpedance.tolerance === 'inTolerance'} onChange={() => handleRadioChange('electricalCheck', 'outputImpedance', 'inTolerance', 'tolerance')} />}
                          label="In Tolerance"
                        />
                        <FormControlLabel
                          control={<Radio checked={protocolData.electricalCheck.outputImpedance.tolerance === 'outTolerance'} onChange={() => handleRadioChange('electricalCheck', 'outputImpedance', 'outTolerance', 'tolerance')} />}
                          label="Out of Tolerance"
                        />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2, width: '100%' }} />
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Insulation Impedance</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Bridge - Body (MΩ)"
                        type="number"
                        inputProps={{ step: "0.01" }}
                        value={protocolData.electricalCheck.insulationImpedance.bridgeBody.value || ''}
                        onChange={(e) => handleInputChange('electricalCheck', 'insulationImpedance', e.target.value, 'bridgeBody.value')}
                      />
                      <FormGroup row sx={{ mt: 1 }}>
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.electricalCheck.insulationImpedance.bridgeBody.tolerance === 'inTolerance'}
                              onChange={() => handleRadioChange('electricalCheck', 'insulationImpedance', 'inTolerance', 'bridgeBody.tolerance')}
                            />
                          }
                          label="In Tolerance"
                        />
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.electricalCheck.insulationImpedance.bridgeBody.tolerance === 'outTolerance'}
                              onChange={() => handleRadioChange('electricalCheck', 'insulationImpedance', 'outTolerance', 'bridgeBody.tolerance')}
                            />
                          }
                          label="Out of Tolerance"
                        />
                      </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Bridge - Shield (MΩ)"
                        type="number"
                        inputProps={{ step: "0.01" }}
                        value={protocolData.electricalCheck.insulationImpedance.bridgeShield.value || ''}
                        onChange={(e) => handleInputChange('electricalCheck', 'insulationImpedance', e.target.value, 'bridgeShield.value')}
                      />
                      <FormGroup row sx={{ mt: 1 }}>
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.electricalCheck.insulationImpedance.bridgeShield.tolerance === 'inTolerance'}
                              onChange={() => handleRadioChange('electricalCheck', 'insulationImpedance', 'inTolerance', 'bridgeShield.tolerance')}
                            />
                          }
                          label="In Tolerance"
                        />
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.electricalCheck.insulationImpedance.bridgeShield.tolerance === 'outTolerance'}
                              onChange={() => handleRadioChange('electricalCheck', 'insulationImpedance', 'outTolerance', 'bridgeShield.tolerance')}
                            />
                          }
                          label="Out of Tolerance"
                        />
                      </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Shield - Body (MΩ)"
                        type="number"
                        inputProps={{ step: "0.01" }}
                        value={protocolData.electricalCheck.insulationImpedance.shieldBody.value || ''}
                        onChange={(e) => handleInputChange('electricalCheck', 'insulationImpedance', e.target.value, 'shieldBody.value')}
                      />
                      <FormGroup row sx={{ mt: 1 }}>
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.electricalCheck.insulationImpedance.shieldBody.tolerance === 'inTolerance'}
                              onChange={() => handleRadioChange('electricalCheck', 'insulationImpedance', 'inTolerance', 'shieldBody.tolerance')}
                            />
                          }
                          label="In Tolerance"
                        />
                        <FormControlLabel
                          control={
                            <Radio
                              checked={protocolData.electricalCheck.insulationImpedance.shieldBody.tolerance === 'outTolerance'}
                              onChange={() => handleRadioChange('electricalCheck', 'insulationImpedance', 'outTolerance', 'shieldBody.tolerance')}
                            />
                          }
                          label="Out of Tolerance"
                        />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2, width: '100%' }} />
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Stability for Small Hits</Typography>
                  <FormGroup row>
                    <FormControlLabel
                      control={<Radio checked={protocolData.electricalCheck.stabilitySmallHits === 'stable'} onChange={() => handleRadioChange('electricalCheck', 'stabilitySmallHits', 'stable')} />}
                      label="Stable"
                    />
                    <FormControlLabel
                      control={<Radio checked={protocolData.electricalCheck.stabilitySmallHits === 'unstable'} onChange={() => handleRadioChange('electricalCheck', 'stabilitySmallHits', 'unstable')} />}
                      label="Unstable"
                    />
                  </FormGroup>
                </Grid>
              </Grid>
            </Paper>
          ) : (
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom color="primary">ELECTRONICS CHECK</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Test</TableCell>
                      <TableCell>Result</TableCell>
                      <TableCell>In Tolerance</TableCell>
                      <TableCell>Out of Tolerance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {protocolData.electronicsCheck ? (
                      <>
                        <TableRow>
                          <TableCell>Main Test</TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              value={protocolData.electronicsCheck.mainTest?.value || ''}
                              onChange={(e) => handleInputChange('electronicsCheck', 'mainTest', e.target.value, 'value')}
                            />
                          </TableCell>
                          <TableCell>
                            <Radio
                              checked={protocolData.electronicsCheck.mainTest?.tolerance === 'inTolerance'}
                              onChange={() => handleRadioChange('electronicsCheck', 'mainTest', 'inTolerance', 'tolerance')}
                            />
                          </TableCell>
                          <TableCell>
                            <Radio
                              checked={protocolData.electronicsCheck.mainTest?.tolerance === 'outTolerance'}
                              onChange={() => handleRadioChange('electronicsCheck', 'mainTest', 'outTolerance', 'tolerance')}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Digital Input Test</TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              value={protocolData.electronicsCheck.digitalInputTest?.value || ''}
                              onChange={(e) => handleInputChange('electronicsCheck', 'digitalInputTest', e.target.value, 'value')}
                            />
                          </TableCell>
                          <TableCell>
                            <Radio
                              checked={protocolData.electronicsCheck.digitalInputTest?.tolerance === 'inTolerance'}
                              onChange={() => handleRadioChange('electronicsCheck', 'digitalInputTest', 'inTolerance', 'tolerance')}
                            />
                          </TableCell>
                          <TableCell>
                            <Radio
                              checked={protocolData.electronicsCheck.digitalInputTest?.tolerance === 'outTolerance'}
                              onChange={() => handleRadioChange('electronicsCheck', 'digitalInputTest', 'outTolerance', 'tolerance')}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Factory Reset</TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              value={protocolData.electronicsCheck.factoryReset?.value || ''}
                              onChange={(e) => handleInputChange('electronicsCheck', 'factoryReset', e.target.value, 'value')}
                            />
                          </TableCell>
                          <TableCell>
                            <Radio
                              checked={protocolData.electronicsCheck.factoryReset?.tolerance === 'inTolerance'}
                              onChange={() => handleRadioChange('electronicsCheck', 'factoryReset', 'inTolerance', 'tolerance')}
                            />
                          </TableCell>
                          <TableCell>
                            <Radio
                              checked={protocolData.electronicsCheck.factoryReset?.tolerance === 'outTolerance'}
                              onChange={() => handleRadioChange('electronicsCheck', 'factoryReset', 'outTolerance', 'tolerance')}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Firmware Update</TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              value={protocolData.electronicsCheck.fwUpdate?.value || ''}
                              onChange={(e) => handleInputChange('electronicsCheck', 'fwUpdate', e.target.value, 'value')}
                            />
                          </TableCell>
                          <TableCell>
                            <Radio
                              checked={protocolData.electronicsCheck.fwUpdate?.tolerance === 'inTolerance'}
                              onChange={() => handleRadioChange('electronicsCheck', 'fwUpdate', 'inTolerance', 'tolerance')}
                            />
                          </TableCell>
                          <TableCell>
                            <Radio
                              checked={protocolData.electronicsCheck.fwUpdate?.tolerance === 'outTolerance'}
                              onChange={() => handleRadioChange('electronicsCheck', 'fwUpdate', 'outTolerance', 'tolerance')}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Notes</TableCell>
                          <TableCell colSpan={3}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              value={protocolData.electronicsCheck.notes || ''}
                              onChange={(e) => handleInputChange('electronicsCheck', 'notes', e.target.value)}
                            />
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4}>No electronics check data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">CONCLUSION STATUS</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Conclusion Status</InputLabel>
                  <Select
                    value={protocolData.conclusion.status || 'Good'}
                    onChange={(e) => handleInputChange('conclusion', 'status', e.target.value)}
                    label="Conclusion Status"
                    sx={{ minWidth: 300 }}
                  >
                    <MenuItem value="Good">Good</MenuItem>
                    <MenuItem value="Defective">Defective</MenuItem>
                    <MenuItem value="Cannot be evaluated">Cannot be evaluated</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">PROBABLE CAUSE OF BREAKAGE</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox checked={protocolData.conclusion.probableCause.mechanicalOverload} onChange={(e) => handleCheckboxChange('conclusion', 'probableCause', 'mechanicalOverload', e.target.checked)} />}
                    label="Mechanical Overload"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={protocolData.conclusion.probableCause.electricalOverload} onChange={(e) => handleCheckboxChange('conclusion', 'probableCause', 'electricalOverload', e.target.checked)} />}
                    label="Electrical Overload"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={protocolData.conclusion.probableCause.waterPenetration} onChange={(e) => handleCheckboxChange('conclusion', 'probableCause', 'waterPenetration', e.target.checked)} />}
                    label="Water Penetration"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={protocolData.conclusion.probableCause.internalComponentBroke} onChange={(e) => handleCheckboxChange('conclusion', 'probableCause', 'internalComponentBroke', e.target.checked)} />}
                    label="Internal Component Broke"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={protocolData.conclusion.probableCause.externalCableBad} onChange={(e) => handleCheckboxChange('conclusion', 'probableCause', 'externalCableBad', e.target.checked)} />}
                    label="External Cable in Bad Condition"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={protocolData.conclusion.probableCause.other.selected} onChange={(e) => handleCheckboxChange('conclusion', 'probableCause', 'other', e.target.checked, 'selected')} />}
                    label="Another Cause"
                  />
                  {protocolData.conclusion.probableCause.other.selected && (
                    <TextField
                      fullWidth
                      label="Other Cause Description"
                      value={protocolData.conclusion.probableCause.other.description || ''}
                      onChange={(e) => handleInputChange('conclusion', 'probableCause', e.target.value, 'other.description')}
                      sx={{ mt: 2 }}
                    />
                  )}
                </FormGroup>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">CONCLUSION OF PROTOCOL</Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Conclusion Text"
              value={protocolData.conclusion.conclusionText || ''}
              onChange={(e) => handleInputChange('conclusion', 'conclusionText', e.target.value)}
              sx={{ mb: 2 }}
            />
          </Paper>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                <strong>Last Saved:</strong> {formatTimestamp(lastSavedAt)}
              </Typography>
              <Typography variant="body2">
                <strong>Last Sent to Customer:</strong> {formatTimestamp(lastSentAt)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Protocol'}
              </Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <Download />}
                onClick={handleDownload}
                disabled={loading || !isProtocolSaved}
              >
                {loading ? 'Downloading...' : 'Download PDF'}
              </Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <Email />}
                onClick={handleSendToCustomer}
                disabled={loading || !isProtocolSaved}
              >
                {loading ? 'Sending...' : 'Send to Customer'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MeasurementProtocolForm;